import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import type { RoleDto } from "client/api/RolesApi";
import type { RoleType } from "client/api/RolesApi";
import type { CreateUserGrantRequest, UserGrantDto } from "client/api/UserGrantApi";
import type { UserDto } from "client/api/UsersApi";
import type { BaseUserGrantsViewProps } from "components/UserGrants/UserGrantsDetailView";
import { RoleSelector } from "../RoleSelector/RoleSelector";
import { UserSelector } from "../UserSelector/UserSelector";
import { SubmittableFormLayout } from "../form/SubmittableFormLayout";

type AddUserGrantViewProps<
  TUserGrant extends UserGrantDto,
  TCreateRequest extends CreateUserGrantRequest
> = BaseUserGrantsViewProps<TUserGrant, TCreateRequest> & {
  roleType: RoleType;
};

type Inputs = {
  user: UserDto;
  role: RoleDto;
};

export function AddUserGrantView<TUserGrant extends UserGrantDto, TCreateRequest extends CreateUserGrantRequest>(
  props: AddUserGrantViewProps<TUserGrant, TCreateRequest>
) {
  const { userGrantsApi, roleType } = props;
  const params = useParams();
  const assetId = params[props.assetIdParamName] || "UNKNOWN_ASSET_ID";

  const navigate = useNavigate();

  const onSubmit = async (params: Inputs) => {
    await userGrantsApi.createUserGrant(assetId, params.user.id, params.role.id);

    //we just navigate backwards by 1 step (for now)
    navigate(-1);
  };

  return (
    <SubmittableFormLayout header="Create new assignment" submitAction={onSubmit}>
      {(formController) => {
        const { register, formState, control, watch } = formController;
        return (
          <Grid container spacing={3}>
            <Grid size={6}>
              <UserSelector
                label="User"
                name="user"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select a user",
                  },
                }}
              />
            </Grid>
            <Grid size={6}>
              <RoleSelector
                label="Role"
                roleType={roleType}
                name="role"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select a role",
                  },
                }}
              />
            </Grid>
          </Grid>
        );
      }}
    </SubmittableFormLayout>
  );
}
